import { parseNumbers } from "./numbers";
import type {
  RecognitionResult,
  RecognizerStatus,
  SpeechRecognizer,
  SpeechRecognizerEvents,
} from "./types";

// Распознавание через ElevenLabs STT (server-side, ключ на сервере).
// Поток: микрофон → VAD режет фразы → POST /api/stt → текст → parseNumbers → движок.
//
// Сегментация: держим лёгкий VAD на AnalyserNode. По началу речи запускаем
// MediaRecorder (свежий файл с валидным заголовком), по тишине HANG_MS —
// останавливаем и шлём. Онсет теряет ~100–130 мс — задокументировано как
// известное ограничение (см. README).

const HANG_MS = 320; // тишина, после которой фраза считается законченной
const MAX_UTTER_MS = 4500; // жёсткий предел длины одной фразы
const MIN_UTTER_MS = 220; // короче — это щелчок/хлопок, не речь
const RMS_ON = 0.020; // порог входа в речь (нормированный RMS)
const RMS_OFF = 0.014; // порог выхода (гистерезис)
const GUARD_AFTER_MUTE_MS = 300; // защитное окно после реплики Кукушки

function pickMime(): string | undefined {
  const c = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return c.find((t) => (window as any).MediaRecorder?.isTypeSupported?.(t));
}

export class ElevenLabsRecognizer implements SpeechRecognizer {
  readonly name = "elevenlabs";
  status: RecognizerStatus = "idle";

  private ev: SpeechRecognizerEvents;
  private stream: MediaStream | null = null;
  private ac: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private buf: Float32Array<ArrayBuffer> | null = null;
  private raf = 0;

  private recorder: MediaRecorder | null = null;
  private mime: string | undefined;
  private chunks: Blob[] = [];

  private speaking = false;
  private muted = false;
  private mutedUntil = 0;
  private speechStartedAt = 0;
  private lastVoiceAt = 0;
  private capStopTimer = 0;
  private running = false;

  constructor(events: SpeechRecognizerEvents) {
    this.ev = events;
  }

  private setStatus(s: RecognizerStatus) {
    this.status = s;
    this.ev.onStatus(s);
  }

  async start(): Promise<void> {
    if (this.running) return;
    if (!navigator.mediaDevices?.getUserMedia || !(window as any).MediaRecorder) {
      throw new Error("mic/MediaRecorder not supported");
    }
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const AC: typeof AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
    this.ac = new AC();
    if (this.ac.state === "suspended") await this.ac.resume();
    const src = this.ac.createMediaStreamSource(this.stream);
    this.analyser = this.ac.createAnalyser();
    this.analyser.fftSize = 1024;
    this.analyser.smoothingTimeConstant = 0.2;
    src.connect(this.analyser);
    this.buf = new Float32Array(this.analyser.fftSize);
    this.mime = pickMime();
    this.running = true;
    this.setStatus("listening");
    this.loop();
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    clearTimeout(this.capStopTimer);
    if (this.recorder && this.recorder.state !== "inactive") this.recorder.stop();
    this.recorder = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.ac?.close();
    this.ac = null;
    this.analyser = null;
    this.setStatus("idle");
  }

  mute(): void {
    this.muted = true;
    // Если сейчас пишем — бросаем запись, это, скорее всего, голос Кукушки.
    if (this.speaking) this.abortCapture();
    this.setStatus("muted");
  }

  unmute(): void {
    this.muted = false;
    this.mutedUntil = performance.now() + GUARD_AFTER_MUTE_MS;
    if (this.running) this.setStatus("listening");
  }

  private rms(): number {
    if (!this.analyser || !this.buf) return 0;
    this.analyser.getFloatTimeDomainData(this.buf);
    let sum = 0;
    for (let i = 0; i < this.buf.length; i++) sum += this.buf[i] * this.buf[i];
    return Math.sqrt(sum / this.buf.length);
  }

  private loop = () => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.loop);
    const now = performance.now();
    const blocked = this.muted || now < this.mutedUntil;
    const level = this.rms();

    if (!this.speaking) {
      if (!blocked && level > RMS_ON) this.beginCapture(now);
      return;
    }

    if (level > RMS_OFF) this.lastVoiceAt = now;
    if (now - this.speechStartedAt > MAX_UTTER_MS) {
      this.endCapture();
    } else if (now - this.lastVoiceAt > HANG_MS) {
      this.endCapture();
    }
  };

  private beginCapture(now: number) {
    if (!this.stream) return;
    this.speaking = true;
    this.speechStartedAt = now;
    this.lastVoiceAt = now;
    this.chunks = [];
    try {
      this.recorder = this.mime
        ? new MediaRecorder(this.stream, { mimeType: this.mime })
        : new MediaRecorder(this.stream);
    } catch {
      this.recorder = new MediaRecorder(this.stream);
    }
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.onstop = () => this.flush();
    this.recorder.start();
    this.setStatus("capturing");
    this.ev.onSpeechStart(Date.now());
  }

  private endCapture() {
    if (this.recorder && this.recorder.state !== "inactive") {
      this.recorder.stop(); // → onstop → flush()
    }
    this.speaking = false;
    this.setStatus("processing");
    this.ev.onSpeechEnd(Date.now());
  }

  private abortCapture() {
    this.speaking = false;
    if (this.recorder && this.recorder.state !== "inactive") {
      this.recorder.onstop = null as any;
      this.recorder.stop();
    }
    this.recorder = null;
    this.chunks = [];
  }

  private async flush() {
    const dur = performance.now() - this.speechStartedAt;
    const type = this.recorder?.mimeType || this.mime || "audio/webm";
    const blob = new Blob(this.chunks, { type });
    this.recorder = null;
    this.chunks = [];
    if (this.running && !this.muted) this.setStatus("listening");

    if (dur < MIN_UTTER_MS || blob.size < 900) return; // слишком коротко — игнор

    const startedAt = Date.now() - Math.round(dur);
    const endedAt = Date.now();
    try {
      const r = await fetch("/api/stt", {
        method: "POST",
        headers: { "Content-Type": type.split(";")[0] },
        body: blob,
      });
      if (!r.ok) {
        this.ev.onError(`stt ${r.status}`);
        return;
      }
      const data = (await r.json()) as { text?: string; ms?: number };
      const rawText = (data.text || "").trim();
      const numbers = parseNumbers(rawText);
      const result: RecognitionResult = {
        numbers,
        rawText,
        startedAt,
        endedAt,
        recognizedAt: Date.now(),
        sttMs: data.ms,
      };
      this.ev.onResult(result);
    } catch (e) {
      this.ev.onError(String((e as Error).message || e));
    }
  }
}
